import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { INDIAN_STATES } from '../../constants/indian-states.constants';
import { LEAD_SOURCE_OPTIONS } from '../../constants/lead-source.constants';
import { LEAD_STREAM_OPTIONS } from '../../constants/lead-stream.constants';
import { PASSPORT_STATUS_OPTIONS } from '../../constants/passport-status.constants';
import type { LeadFormFieldErrors, LeadFormFields } from '../../models/lead-form.model';

@Component({
  selector: 'app-lead-form-fields',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="cg-admin-form-grid cg-admin-form-grid--2">
      <label class="block text-sm">
        <span class="text-slate-700">Full name <span class="field-required">*</span></span>
        <input
          class="mt-1 w-full rounded border p-2"
          [class.field-invalid]="fieldErrors.fullName"
          [(ngModel)]="form.fullName"
          name="fullName"
          required
        />
        @if (fieldErrors.fullName) {
          <p class="field-error">{{ fieldErrors.fullName }}</p>
        }
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Email</span>
        <input
          class="mt-1 w-full rounded border p-2"
          type="email"
          [class.field-invalid]="fieldErrors.email"
          [(ngModel)]="form.email"
          name="email"
        />
        @if (fieldErrors.email) {
          <p class="field-error">{{ fieldErrors.email }}</p>
        }
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Phone <span class="field-required">*</span></span>
        <input
          class="mt-1 w-full rounded border p-2"
          [class.field-invalid]="fieldErrors.phoneNumber"
          [(ngModel)]="form.phoneNumber"
          name="phoneNumber"
          required
        />
        @if (fieldErrors.phoneNumber) {
          <p class="field-error">{{ fieldErrors.phoneNumber }}</p>
        }
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Preferred destination <span class="field-required">*</span></span>
        <select
          class="mt-1 w-full rounded border p-2"
          [class.field-invalid]="fieldErrors.preferredDestination"
          [(ngModel)]="form.preferredDestination"
          name="preferredDestination"
          required
        >
          <option value="">Select a country</option>
          @for (d of destinationOptions; track d) {
            <option [value]="d">{{ d }}</option>
          }
        </select>
        @if (fieldErrors.preferredDestination) {
          <p class="field-error">{{ fieldErrors.preferredDestination }}</p>
        }
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">WhatsApp</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.whatsappNumber" name="whatsappNumber" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Lead source <span class="field-required">*</span></span>
        <select
          class="mt-1 w-full rounded border p-2"
          [class.field-invalid]="fieldErrors.leadSource"
          [(ngModel)]="form.leadSource"
          name="leadSource"
          required
        >
          <option value="">Select a source</option>
          @for (source of leadSourceOptions; track source) {
            <option [value]="source">{{ source }}</option>
          }
        </select>
        @if (fieldErrors.leadSource) {
          <p class="field-error">{{ fieldErrors.leadSource }}</p>
        }
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Stream</span>
        <select class="mt-1 w-full rounded border p-2" [(ngModel)]="form.stream" name="stream">
          <option value="">Select stream</option>
          @for (s of streamOptions; track s) {
            <option [value]="s">{{ s }}</option>
          }
        </select>
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Section</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.section" name="section" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">City</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.city" name="city" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">State</span>
        <select class="mt-1 w-full rounded border p-2" [(ngModel)]="form.state" name="state">
          <option value="">Select a state</option>
          @for (s of stateOptions; track s) {
            <option [value]="s">{{ s }}</option>
          }
        </select>
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Country</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.country" name="country" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Qualification</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.qualification" name="qualification" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Percentage / CGPA</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.percentageOrCgpa" name="percentageOrCgpa" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Passing year</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.passingYear" name="passingYear" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Institution</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.institution" name="institution" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Preferred course</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.preferredCourse" name="preferredCourse" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Intake</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.intake" name="intake" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Budget range</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.budgetRange" name="budgetRange" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Passport status</span>
        <select class="mt-1 w-full rounded border p-2" [(ngModel)]="form.passportStatus" name="passportStatus">
          <option value="">Select status</option>
          @for (p of passportStatusOptions; track p) {
            <option [value]="p">{{ p }}</option>
          }
        </select>
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">English test status</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.englishTestStatus" name="englishTestStatus" />
      </label>
      <label class="block text-sm">
        <span class="text-slate-700">Loan required</span>
        <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.loanRequired" name="loanRequired" />
      </label>
      <label class="block text-sm" style="grid-column: 1 / -1">
        <span class="text-slate-700">Message</span>
        <textarea class="mt-1 w-full rounded border p-2" rows="3" [(ngModel)]="form.message" name="message"></textarea>
      </label>
    </div>
  `,
  styles: `
    .field-required {
      color: #dc2626;
    }
    .field-error {
      margin-top: 0.25rem;
      font-size: 0.75rem;
      color: #dc2626;
    }
    .field-invalid {
      border-color: #dc2626;
      outline: 1px solid #fca5a5;
    }
  `,
})
export class LeadFormFieldsComponent {
  @Input({ required: true }) form!: LeadFormFields;
  @Input() fieldErrors: LeadFormFieldErrors = {};
  @Input() destinationOptions: string[] = [];
  @Input() stateOptions: string[] = [...INDIAN_STATES];
  @Input() passportStatusOptions: string[] = [...PASSPORT_STATUS_OPTIONS];
  readonly leadSourceOptions = LEAD_SOURCE_OPTIONS;
  readonly streamOptions = LEAD_STREAM_OPTIONS;
}
